import { RegexRule } from '../types';

/** Minimum length required for new passwords (sign-up, reset, change password). */
export const PASSWORD_MIN_LENGTH = 12;

export type PasswordRuleName = 'min_length' | 'uppercase' | 'lowercase' | 'numbers' | 'special';

/** Stable display order for password checklist rows. */
export const PASSWORD_RULE_ORDER: PasswordRuleName[] = ['min_length', 'uppercase', 'numbers', 'special', 'lowercase'];

export type PasswordFieldTranslateFn = (key: string, options?: Record<string, string | number>) => string;

export const regexRules: RegexRule[] = [
  {
    pattern: /([A-Z])/,
    name: 'uppercase',
  },
  {
    pattern: /([a-z])/,
    name: 'lowercase',
  },
  {
    pattern: /([0-9])/,
    name: 'numbers',
  },
  {
    pattern: /([^A-Za-z0-9])/,
    name: 'special',
  },
];
/**
 * Utility function to check how many rules (regex) the
 * passed value (password) conforms to
 *
 * @param value - the value to validate
 * @param rules - the set of rules to validate against
 *
 * @returns array of rule names that could not be matched
 */
export const checkUnmatchedRules = (value: string, rules: RegexRule[]): string[] => {
  const errors = rules.reduce<string[]>((prev, curr) => {
    if (!curr.pattern.test(value)) {
      return [...prev, curr.name];
    }
    return prev;
  }, []);
  return errors;
};

/**
 * Returns all password rules that the value fails to satisfy.
 *
 * @param value - password field value
 * @returns failed rule names (length and character classes)
 */
export const getPasswordValidationFailures = (value: string): PasswordRuleName[] => {
  const failures: PasswordRuleName[] = [];

  if (value.length < PASSWORD_MIN_LENGTH) {
    failures.push('min_length');
  }

  const unmatched = checkUnmatchedRules(value, regexRules);
  for (const ruleName of unmatched) {
    failures.push(ruleName as PasswordRuleName);
  }

  return failures;
};

/**
 * Validates a password for react-hook-form `rules.validate`.
 * Requires {@link PASSWORD_MIN_LENGTH} characters and every {@link regexRules} class to match.
 *
 * @param value - password field value
 * @param t - translation function
 * @returns `true` if valid, otherwise the generic weak-password error string
 */
export const validatePasswordField = (value: string, t: PasswordFieldTranslateFn): true | string => {
  return getPasswordValidationFailures(value).length === 0 ? true : t('ns.common:password:error:weak');
};
